import CalendarShare from "../../entities/calendarShare";

/**
 * CalendarShare actions
 *
 * Call order:
 *
 * CALENDARSHARE_SAVE_QUEUE:	adds the item to the save queue
 * --> CALENDARSHARE_SAVE:	modifies the state
 * --> CALENDARSHARE_SAVE_SUCCESS: if all went well
 * --> CALENDARSHARE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail CALENDARSHARE_SAVE_QUEUE is during the call to CALENDARSHARE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at CALENDARSHARE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: CALENDARSHARE_SAVE_SUCCESS, CALENDARSHARE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or CALENDARSHARE_PENDING_CHECK (it left the queue).
 */

export const CALENDARSHARE_FETCH_ONE_QUEUE: any = 'CALENDARSHARE_FETCH_ONE_QUEUE';
export const CALENDARSHARE_FETCH_SUCCESS: any = 'CALENDARSHARE_FETCH_SUCCESS';
export const CALENDARSHARE_FETCH_FAILURE: any = 'CALENDARSHARE_FETCH_FAILURE';
export const CALENDARSHARE_FETCH_ONE_SUCCESS: any = 'CALENDARSHARE_FETCH_ONE_SUCCESS';
export const CALENDARSHARE_FETCH_ONE_FAILURE: any = 'CALENDARSHARE_FETCH_ONE_FAILURE';

export const CALENDARSHARE_SAVE: any = 'CALENDARSHARE_SAVE';
export const CALENDARSHARE_SAVE_QUEUE: any = 'CALENDARSHARE_SAVE_QUEUE';
export const CALENDARSHARE_SAVE_SUCCESS: any = 'CALENDARSHARE_SAVE_SUCCESS';
export const CALENDARSHARE_SAVE_FAILURE: any = 'CALENDARSHARE_SAVE_FAILURE';

export const CALENDARSHARE_SAVE_MANY: any = 'CALENDARSHARE_SAVE_MANY';
export const CALENDARSHARE_SAVE_MANY_QUEUE: any = 'CALENDARSHARE_SAVE_MANY_QUEUE';
export const CALENDARSHARE_SAVE_MANY_SUCCESS: any = 'CALENDARSHARE_SAVE_MANY_SUCCESS';
export const CALENDARSHARE_SAVE_MANY_FAILURE: any = 'CALENDARSHARE_SAVE_MANY_FAILURE';

export const CALENDARSHARE_REMOVE: any = 'CALENDARSHARE_REMOVE';
export const CALENDARSHARE_REMOVE_QUEUE: any = 'CALENDARSHARE_REMOVE_QUEUE';
export const CALENDARSHARE_REMOVE_SUCCESS: any = 'CALENDARSHARE_REMOVE_SUCCESS';
export const CALENDARSHARE_REMOVE_FAILURE: any = 'CALENDARSHARE_REMOVE_FAILURE';

export const CALENDARSHARE_RESTORE: any = 'CALENDARSHARE_RESTORE';
export const CALENDARSHARE_RESTORE_QUEUE: any = 'CALENDARSHARE_RESTORE_QUEUE';
export const CALENDARSHARE_RESTORE_SUCCESS: any = 'CALENDARSHARE_RESTORE_SUCCESS';
export const CALENDARSHARE_RESTORE_FAILURE: any = 'CALENDARSHARE_RESTORE_FAILURE';

export const CALENDARSHARE_RESTORE_MANY: any = 'CALENDARSHARE_RESTORE_MANY';
export const CALENDARSHARE_REMOVE_MANY: any = 'CALENDARSHARE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const CALENDARSHARE_SAVE_REVERT: any = 'CALENDARSHARE_SAVE_REVERT';
export const CALENDARSHARE_PENDING_CHECK: any = 'CALENDARSHARE_PENDING_CHECK';

/* Fetch actions */
export const fetchCalendarShareSuccess = (calendarShares: CalendarShare[], partial: boolean, lastFetch: number) => ({type: CALENDARSHARE_FETCH_SUCCESS, calendarShares, partial, lastFetch});
export const fetchCalendarShareFailure = (err: any) => {
		console.error('CalendarShareAction: fetchCalendarShareFailure', err);
		return {type: CALENDARSHARE_FETCH_FAILURE, err};
};
export const fetchOneCalendarShareQueue = (calendarShareId: string, sentAddedOn?: number) => ({type: CALENDARSHARE_FETCH_ONE_QUEUE, calendarShareId, sentAddedOn});
export const fetchOneCalendarShareSuccess = (calendarShare: CalendarShare | null, sentAddedOn?: number, calendarShareId: string = calendarShare?.id) => ({type: CALENDARSHARE_FETCH_ONE_SUCCESS, calendarShareId, calendarShare, sentAddedOn});
export const fetchOneCalendarShareFailure = (id: string, err: any) => {
		console.error('CalendarShareAction: fetchOneCalendarShareFailure', err);
		return {type: CALENDARSHARE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCalendarShare = (calendarShare: CalendarShare) => {
    // console.debug('⮂ CALENDARSHARE_SAVE action', calendarShare?.id);
    return {type: CALENDARSHARE_SAVE, calendarShare};
}
export const saveCalendarShareQueue = (calendarShare: CalendarShare, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CALENDARSHARE_SAVE_QUEUE action', calendarShare?.id);
    return {type: CALENDARSHARE_SAVE_QUEUE, calendarShare, onQueued};
}
export const saveCalendarShareSuccess = (calendarShare: CalendarShare, sentAddedOn?: number) => {
    // console.debug('⮂ CALENDARSHARE_SAVE_SUCCESS action', calendarShare?.id);
    return {type: CALENDARSHARE_SAVE_SUCCESS, calendarShare, sentAddedOn};
}
export const saveCalendarShareFailure = (calendarShare: CalendarShare, status: number, message: string) => {
    console.warn(`⮂ CALENDARSHARE_SAVE_FAILURE error saving calendarShare: HTTP ${status}: ${message}`, calendarShare?.id);
    return {type: CALENDARSHARE_SAVE_FAILURE, calendarShare, status, message};
};

/* SaveMany actions */
export const saveManyCalendarShares = (calendarShares: CalendarShare[]) => {
    // console.debug('⮂ CALENDARSHARE_SAVE_MANY action', calendarShares?.map(calendarShare => calendarShare?.id).join(','));
    return {type: CALENDARSHARE_SAVE_MANY, calendarShares};
}
export const saveManyCalendarSharesQueue = (calendarShares: CalendarShare[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CALENDARSHARE_SAVE_MANY_QUEUE action', calendarShares?.map(calendarShare => calendarShare.id).join(','));
    return {type: CALENDARSHARE_SAVE_MANY_QUEUE, calendarShares, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `calendarShares` (OWA-1018) */
export const saveManyCalendarSharesSuccess = (calendarShares: CalendarShare[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ CALENDARSHARE_SAVE_MANY_SUCCESS action', calendarShares?.map(calendarShare => calendarShare.id).join(','));
    return {type: CALENDARSHARE_SAVE_MANY_SUCCESS, calendarShares, sentAddedOns};
}
export const saveManyCalendarSharesFailure = (calendarShares: CalendarShare[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_CALENDARSHARES_FAILURE Error saving calendarShares: HTTP ${status}: ${message}`, calendarShares?.map(calendarShare => calendarShare.id).join(','));
		return {type: CALENDARSHARE_SAVE_MANY_FAILURE, calendarShares, status, message};
};

/* Remove actions */
export const removeCalendarShare = (calendarShare: CalendarShare) => ({ type: CALENDARSHARE_REMOVE, calendarShare });
export const removeCalendarShareQueue = (calendarShare: CalendarShare) => ({ type: CALENDARSHARE_REMOVE_QUEUE, calendarShare });
export const removeCalendarShareSuccess = (calendarShare: CalendarShare) => ({ type: CALENDARSHARE_REMOVE_SUCCESS, calendarShare });
export const removeCalendarShareFailure = (calendarShare: CalendarShare, status: number, message: string) => {
	console.error(`Redux: removeCalendarShareFailure Error removing calendarShare: HTTP ${status}: ${message}`);
	return ({ type: CALENDARSHARE_REMOVE_FAILURE, calendarShare, status, message });
};

/* Restore actions */
export const restoreCalendarShare = (calendarShare: CalendarShare) => ({ type: CALENDARSHARE_RESTORE, calendarShare });
export const restoreCalendarShareQueue = (calendarShare: CalendarShare) => ({ type: CALENDARSHARE_RESTORE_QUEUE, calendarShare });
export const restoreCalendarShareSuccess = (calendarShare: CalendarShare, sentAddedOn?: number) => ({ type: CALENDARSHARE_RESTORE_SUCCESS, calendarShare, sentAddedOn });
export const restoreCalendarShareFailure = (calendarShare: CalendarShare, status: number, message: string) => {
	console.error(`Redux: restoreCalendarShareFailure Error restoring calendarShare: HTTP ${status}: ${message}`);
	return ({ type: CALENDARSHARE_RESTORE_FAILURE, calendarShare, status, message });
};

/* RestoreMany actions */
export const restoreManyCalendarShares = (calendarShares: CalendarShare[]) => ({ type: CALENDARSHARE_RESTORE_MANY, calendarShares });

/* RemoveMany actions */
export const removeManyCalendarShares = (calendarShares: CalendarShare[]) => ({ type: CALENDARSHARE_REMOVE_MANY, calendarShares });

/* Pending save actions (OWA-1018) */
/**
 * The save `calendarShare` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCalendarShareSave = (calendarShare: CalendarShare, options: {sentAddedOn?: number, current?: CalendarShare | null, keep?: boolean} = {}) =>
    ({type: CALENDARSHARE_SAVE_REVERT, calendarShare, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCalendarSharePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: CALENDARSHARE_PENDING_CHECK, live, refused, readAt});